/* kaleidoscope/index.js — DOM orchestrator for the Kaleidoscope drawing pad.
 *
 * The only DOM-coupled module. Live drawing is incremental (each pointer move
 * strokes only the new segment through every transform); the rAF loop runs
 * ONLY while auto-draw is active.
 */

// Versioned import: reuse this module's own ?v= cache-buster so a deploy's
// fresh index.js can never pair with a stale, immutable-cached symmetry.js on
// the intl edge (180-day immutable there — a plain relative import would never
// refetch). Same pattern as lang/level-test-app.js. symmetry.js carries the
// geometry, so a fix to it must actually reach returning visitors.
/* Статический импорт — тот же случай, что и в js/lang/level-test-app.js:
 * верхнеуровневый await не разбирается ниже Chrome 89 / Safari 15, а ручная
 * передача ?v= не нужна под moduleGraph (версия в пути). Остальные восемь
 * соседей ниже импортируются статически с самого начала. */
import { makeTransforms, imageCount } from './symmetry.js';
import { BRUSHES } from './brushes.js';
import {
    createStore, beginStroke, addPoint, commit, undo, redo, clear,
    strokeCount, pointCount,
} from './strokes.js';
import { generateStroke } from './autodraw.js';
import { drawStroke, repaint, drawGuides } from './render.js';
import { strokesToSvg, SVG_STROKE_LIMIT } from './svg-export.js';
import { dpr, reducedMotion } from '../toy-fx/prefs.js';
import { initFunnel } from '../toy-fx/telemetry.js';
import { PALETTES } from '../toy-fx/palette.js';
import { exportPng, svgToBlob, downloadBlob } from '../toy-fx/canvas-export.js';

const SLUG = 'kaleidoscope';
const I18N = (typeof window !== 'undefined' && window.KaleidoscopeI18n) || {};
const t = (k, f) => (I18N && I18N[k]) || f || k;
const $ = (id) => document.getElementById(id);
function ae(ev, p) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, p || {})); } catch (_) {} }

export function mount() {
    const stage = $('kal-stage');
    const canvas = $('kal-canvas');
    const guideCanvas = $('kal-guides');
    if (!stage || !canvas) return;
    const ctx = canvas.getContext('2d');
    const gctx = guideCanvas ? guideCanvas.getContext('2d') : null;

    const state = {
        store: createStore(),
        n: 8,
        mirror: true,
        guides: true,
        brush: 'round',
        size: 10,
        color: '#7ad8ff',
        rainbow: false,
        erase: false,
        bgColor: '#0e1320',
        bgStyle: 'solid',
        transforms: [],
        autodraw: false,
    };
    let rafId = 0, w = 0, h = 0, cx = 0, cy = 0, radius = 0;
    let current = null, lastPt = null;
    let autoTimer = 0, autoSeed = 1;

    /* ---- settings ---- */
    function restore() {
        try {
            const ts = window.__toolSettings; if (!ts) return;
            state.n = parseInt(ts.load(SLUG, 'n', '8'), 10) || 8;
            state.mirror = ts.load(SLUG, 'mirror', '1') !== '0';
            state.guides = ts.load(SLUG, 'guides', '1') !== '0';
            if (BRUSHES.indexOf(ts.load(SLUG, 'brush', '')) >= 0) state.brush = ts.load(SLUG, 'brush', 'round');
            state.size = parseInt(ts.load(SLUG, 'size', '10'), 10) || 10;
            state.color = ts.load(SLUG, 'color', '#7ad8ff');
            state.rainbow = ts.load(SLUG, 'rainbow', '0') === '1';
            state.bgColor = ts.load(SLUG, 'bg', '#0e1320');
            state.bgStyle = ts.load(SLUG, 'bgstyle', 'solid');
        } catch (_) {}
    }
    function save(k, v) { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) {} }

    function rebuildTransforms() {
        state.transforms = makeTransforms(state.n, state.mirror, 0);
    }

    /* ---- geometry ---- */
    function resize() {
        const rect = stage.getBoundingClientRect();
        const size = Math.max(220, Math.min(rect.width, rect.height || rect.width));
        const r = dpr();
        for (const cv of [canvas, guideCanvas]) {
            if (!cv) continue;
            cv.width = Math.round(size * r);
            cv.height = Math.round(size * r);
            cv.style.width = size + 'px';
            cv.style.height = size + 'px';
        }
        ctx.setTransform(r, 0, 0, r, 0, 0);
        if (gctx) gctx.setTransform(r, 0, 0, r, 0, 0);
        w = size; h = size;
        cx = size / 2; cy = size / 2;
        radius = size / 2;
        fullRepaint();
        drawGuideLayer();
    }

    function bgSpec() {
        return { style: state.bgStyle, color: state.bgColor, inner: '#1b2740' };
    }

    function fullRepaint() {
        try { repaint(ctx, state.store, state.transforms, cx, cy, w, h, bgSpec()); }
        catch (e) { if (window.__handleError) window.__handleError(e, 'kaleidoscope.repaint'); }
    }

    function drawGuideLayer() {
        if (!gctx) return;
        gctx.setTransform(dpr(), 0, 0, dpr(), 0, 0);
        gctx.clearRect(0, 0, w, h);
        if (state.guides) drawGuides(gctx, state.n, cx, cy, radius);
    }

    /* ---- live drawing ---- */
    function toCentre(clientX, clientY) {
        const rect = canvas.getBoundingClientRect();
        return {
            x: (clientX - rect.left) / rect.width * w - cx,
            y: (clientY - rect.top) / rect.height * h - cy,
        };
    }

    function startStroke(clientX, clientY, pressure) {
        if (state.autodraw) stopAutodraw();
        current = beginStroke(state);
        const p = toCentre(clientX, clientY);
        addPoint(current, p.x, p.y, pressure);
        lastPt = p;
    }
    function extendStroke(clientX, clientY, pressure) {
        if (!current) return;
        const p = toCentre(clientX, clientY);
        addPoint(current, p.x, p.y, pressure);
        // Incremental: stroke only the new segment through every transform.
        const seg = {
            brush: current.brush, color: current.color, size: current.size,
            rainbow: current.rainbow, erase: current.erase,
            points: [lastPt, { x: p.x, y: p.y, p: pressure == null ? 1 : pressure }],
        };
        try { drawStroke(ctx, seg, state.transforms, cx, cy); }
        catch (e) { if (window.__handleError) window.__handleError(e, 'kaleidoscope.draw'); }
        lastPt = { x: p.x, y: p.y };
    }
    function endStroke() {
        if (!current) return;
        if (commit(state.store, current)) {
            updateCounters();
            ae('feature_use', { f: 'first_stroke' });
            try { window.__aeToolComplete && window.__aeToolComplete(); } catch (_) {}
        }
        current = null;
        lastPt = null;
    }

    /* ---- auto-draw ---- */
    function startAutodraw() {
        state.autodraw = true;
        const btn = $('kal-autodraw');
        if (btn) btn.textContent = t('autodraw_stop', 'Stop auto-draw');
        ae('feature_use', { f: 'autodraw_start' });
        const tick = () => {
            if (!state.autodraw) return;
            const stroke = beginStroke(state);
            const gen = generateStroke(autoSeed++, radius * 0.92, 70);
            stroke.points = gen.points;
            stroke.rainbow = true;
            commit(state.store, stroke);
            drawStroke(ctx, stroke, state.transforms, cx, cy);
            updateCounters();
            autoTimer = setTimeout(tick, 720);
        };
        tick();
    }
    function stopAutodraw() {
        state.autodraw = false;
        if (autoTimer) { clearTimeout(autoTimer); autoTimer = 0; }
        const btn = $('kal-autodraw');
        if (btn) btn.textContent = t('autodraw', 'Auto-draw');
    }

    /* ---- ui ---- */
    function updateCounters() {
        const el = $('kal-info');
        if (el) {
            el.textContent = t('info', '{c} copies · {s} strokes')
                .replace('{c}', imageCount(state.n, state.mirror))
                .replace('{s}', strokeCount(state.store));
        }
        const u = $('kal-undo'), r = $('kal-redo');
        if (u) u.disabled = strokeCount(state.store) === 0;
        if (r) r.disabled = state.store.redo.length === 0;
    }

    function buildPalette() {
        const host = $('kal-palette');
        if (!host) return;
        host.innerHTML = '';
        PALETTES.vivid.concat(PALETTES.pastel.slice(0, 4)).forEach((hex) => {
            const sw = document.createElement('button');
            sw.type = 'button';
            sw.className = 'kal-swatch';
            sw.style.background = hex;
            sw.setAttribute('aria-label', hex);
            sw.addEventListener('click', () => {
                state.color = hex;
                const ci = $('kal-color');
                if (ci) ci.value = hex;
                const rb = $('kal-rainbow');
                if (rb && rb.checked) { rb.checked = false; state.rainbow = false; }
                save('color', hex);
            });
            host.appendChild(sw);
        });
    }

    /* ---- exports ---- */
    function exportPNG() {
        const scale = parseInt(($('kal-scale') || {}).value || '2', 10);
        exportPng((c, cw, ch) => {
            repaint(c, state.store, makeTransforms(state.n, state.mirror, 0),
                cw / 2, ch / 2, cw, ch, bgSpec());
        }, {
            w: 1000, h: 1000, scale: scale,
            transparent: state.bgStyle === 'transparent',
            bg: state.bgColor,
        }).then((blob) => {
            downloadBlob(blob, 'kaleidoscope.png');
            ae('feature_use', { f: 'export', format: 'png', scale: scale });
        }).catch((e) => { if (window.__handleError) window.__handleError(e, 'kaleidoscope.png'); });
    }
    function exportSVG() {
        if (pointCount(state.store) > SVG_STROKE_LIMIT * 40 || strokeCount(state.store) > SVG_STROKE_LIMIT) {
            if (window.__toast) window.__toast(t('svg_too_big', 'Drawing too detailed for SVG — use PNG'), 'warning');
            return;
        }
        try {
            const svg = strokesToSvg(state.store, {
                size: 1000, n: state.n, mirror: state.mirror,
                background: state.bgColor, bgStyle: state.bgStyle,
            });
            downloadBlob(svgToBlob(svg), 'kaleidoscope.svg');
            ae('feature_use', { f: 'export', format: 'svg' });
        } catch (e) { if (window.__handleError) window.__handleError(e, 'kaleidoscope.svg'); }
    }

    /* ---- wiring ---- */
    function wire() {
        canvas.addEventListener('pointerdown', (e) => {
            canvas.setPointerCapture(e.pointerId);
            startStroke(e.clientX, e.clientY, e.pressure || 1);
        });
        canvas.addEventListener('pointermove', (e) => {
            if (current) extendStroke(e.clientX, e.clientY, e.pressure || 1);
        });
        const up = () => endStroke();
        canvas.addEventListener('pointerup', up);
        canvas.addEventListener('pointercancel', up);

        const seg = $('kal-segments');
        if (seg) {
            seg.value = state.n;
            seg.addEventListener('input', () => {
                state.n = parseInt(seg.value, 10) || 8;
                save('n', String(state.n));
                rebuildTransforms(); fullRepaint(); drawGuideLayer(); updateCounters();
                ae('feature_use', { f: 'segments_change', n: state.n });
            });
        }
        bindToggle('kal-mirror', 'mirror', () => {
            save('mirror', state.mirror ? '1' : '0');
            rebuildTransforms(); fullRepaint(); updateCounters();
            ae('feature_use', { f: 'mirror_toggle' });
        });
        bindToggle('kal-guides-toggle', 'guides', () => {
            save('guides', state.guides ? '1' : '0'); drawGuideLayer();
        });
        bindSelect('kal-brush', 'brush', () => { save('brush', state.brush); ae('feature_use', { f: 'brush_select' }); });
        bindRange('kal-size', 'size', () => save('size', String(state.size)));
        const color = $('kal-color');
        if (color) {
            color.value = state.color;
            color.addEventListener('input', () => { state.color = color.value; save('color', color.value); });
        }
        bindToggle('kal-rainbow', 'rainbow', () => { save('rainbow', state.rainbow ? '1' : '0'); ae('feature_use', { f: 'rainbow_toggle' }); });
        bindToggle('kal-eraser', 'erase', null);
        const bg = $('kal-bg');
        if (bg) {
            bg.value = state.bgColor;
            bg.addEventListener('input', () => { state.bgColor = bg.value; save('bg', bg.value); fullRepaint(); });
        }
        bindSelect('kal-bgstyle', 'bgStyle', () => { save('bgstyle', state.bgStyle); fullRepaint(); });

        bindClick('kal-undo', () => { if (undo(state.store)) { fullRepaint(); updateCounters(); } });
        bindClick('kal-redo', () => { if (redo(state.store)) { fullRepaint(); updateCounters(); } });
        bindClick('kal-clear', () => {
            clear(state.store); fullRepaint(); updateCounters();
            if (window.__announce) window.__announce(t('cleared', 'Drawing cleared'));
            ae('feature_use', { f: 'clear' });
        });
        bindClick('kal-autodraw', () => { state.autodraw ? stopAutodraw() : startAutodraw(); });
        bindClick('kal-png', exportPNG);
        bindClick('kal-svg', exportSVG);

        window.addEventListener('resize', debounce(resize, 150));
        window.addEventListener('beforeunload', () => {
            stopAutodraw();
            if (rafId) cancelAnimationFrame(rafId);
        });
    }
    function bindClick(id, fn) { const el = $(id); if (el) el.addEventListener('click', fn); }
    function bindToggle(id, key, fn) {
        const el = $(id); if (!el) return;
        el.checked = !!state[key];
        el.addEventListener('change', () => { state[key] = el.checked; if (fn) fn(); });
    }
    function bindSelect(id, key, fn) {
        const el = $(id); if (!el) return;
        el.value = state[key];
        el.addEventListener('change', () => { state[key] = el.value; if (fn) fn(); });
    }
    function bindRange(id, key, fn) {
        const el = $(id); if (!el) return;
        el.value = state[key];
        el.addEventListener('input', () => { state[key] = parseInt(el.value, 10) || 1; if (fn) fn(); });
    }

    /* ---- boot ---- */
    restore();
    rebuildTransforms();
    wire();
    buildPalette();
    resize();
    updateCounters();
    try { initFunnel(SLUG); } catch (_) {}

    window.__kaleidoDebug = {
        state: state,
        strokeCount: () => strokeCount(state.store),
        drawStroke: (pts) => {
            const s = beginStroke(state);
            s.points = pts.map((p) => ({ x: p[0], y: p[1], p: 1 }));
            commit(state.store, s);
            fullRepaint();
            updateCounters();
        },
        exportSvg: () => strokesToSvg(state.store, { size: 1000, n: state.n, mirror: state.mirror }),
        reducedMotion: reducedMotion,
    };
}

function debounce(fn, ms) {
    let timer = 0;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
    else mount();
}
